/**
 * CategoryTransferTable Component
 * Renders a collapsible section table for a single transfer-out category
 * (Finished Products, Rejected Materials, Remaining Materials, By-Products).
 * Extracted from DailyTransferOutView's renderCategoryTable function.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';
import { ChevronDown, ChevronRight, Printer } from 'lucide-react';
import { DailyTransferOutItem, PrintHistoryEntry } from '../../../services/dailyTransferOutService';
import PrintAuditPopover from './PrintAuditPopover';

interface CategoryTransferTableProps {
  sectionKey: string;
  title: string;
  icon: React.ReactNode;
  items: DailyTransferOutItem[];
  showExpected: boolean;
  headerAction?: React.ReactNode;
  onToggleExclude?: (item: DailyTransferOutItem) => void;
  // WCO-2623: per-row supervisor Reprint override (shown only on already-printed FP-NI/RM-NI rows).
  onReprint?: (item: DailyTransferOutItem) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  getRowStatus: (item: DailyTransferOutItem) => string;
  getStatusBadge: (status: string) => React.ReactNode;
  getItemYieldVariance: (item: DailyTransferOutItem) => number | null;
  getItemActualYF: (item: DailyTransferOutItem) => number | null;
  getActualYFColor: (item: DailyTransferOutItem) => string;
  getYieldVarianceColor: (variancePct: number | null, thresholdDecimal?: number) => string;
  formatYieldVariancePct: (variancePct: number | null) => string;
}

// WCO-2502: a By-Products row can be a split variant of a marketable by-product.
// `_variant` marks it as the Cases or Buffer card; `_key` is a stable React key.
// Both variant cards map to the same underlying DTO item and its single `excluded` flag.
// WCO-2582: only the Cases card is a transfer decision (renders the checkbox); the Buffer/reserve
// card is read-only carry-forward and shows NO checkbox.
type DisplayItem = DailyTransferOutItem & {
  _variant?: 'cases' | 'buffer';
  _key: string;
};

const CategoryTransferTable: React.FC<CategoryTransferTableProps> = ({
  sectionKey,
  title,
  icon,
  items,
  showExpected,
  headerAction,
  onToggleExclude,
  onReprint,
  isCollapsed,
  onToggleCollapse,
  getRowStatus,
  getStatusBadge,
  getItemYieldVariance,
  getItemActualYF,
  getActualYFColor,
  getYieldVarianceColor,
  formatYieldVariancePct,
}) => {
  const { t } = useLanguage();
  const showReserveColumn = items.some((item) => item.category === 'by_product_marketable');

  // WCO-2618: the (i) print-audit and the PRINTED status apply ONLY to rows that actually require a
  // printed label — the same rows with a "Print Labels" button. This is a PER-ROW decision, not
  // per-section, because the "remaining raw material" section mixes types: only remainder_not_intact
  // (RM-NI) needs a label; unused_lot / remainder_intact rows are intact and need no print.
  //   • FP-NI: every row needs an NI label.
  //   • remaining: only remainder_not_intact.
  //   • by-products (marketable): every Cases/Buffer row (has a by-product label).
  const rowNeedsLabel = (item: DailyTransferOutItem): boolean => {
    if (sectionKey === 'finishedNotIntact') return true;
    if (sectionKey === 'remaining') return item.remaining_type === 'remainder_not_intact';
    if (sectionKey === 'byproducts') return item.category === 'by_product_marketable';
    return false;
  };
  // Show the PRINTED column header when ANY row in the section needs a label.
  const showPrinted = items.some(rowNeedsLabel);
  // Whether a row's label is printed:
  //  • marketable by-products split into Cases/Buffer rows, each with its OWN print state — the backend
  //    attaches cases_printed (a case has printed_at, not voided) and buffer_printed (buffer status=printed).
  //    has_label is NOT "printed" here (it means "nothing still queued in 'ready'"), so we must not use it.
  //  • NI / remaining rows use has_label (the WCO-2581 "printed AND still up-to-date" signal).
  const isPrinted = (item: DisplayItem) => {
    if (item.category === 'by_product_marketable') {
      return item._variant === 'buffer' ? item.buffer_printed === true : item.cases_printed === true;
    }
    return item.has_label === true;
  };

  // WCO-2618: the print history shown in the (i) popover for a row.
  //  • NI / remaining rows carry the real lot-label history (print/reprint/supersede) from the backend.
  //  • Marketable by-products now carry a real single-entry history per variant from the backend
  //    (print_history_cases / print_history_buffer — printed_at + resolved printed_by from the
  //    marketable case / buffer). Pick the one matching this split card's _variant.
  //  • Fall back to a synthesized weight-only entry if the backend has no history but the row is
  //    printed (grandfathered rows with no printed_at/by), or [] (→ "No labels printed yet").
  const popoverHistory = (item: DisplayItem): PrintHistoryEntry[] => {
    if (item.category !== 'by_product_marketable') return item.print_history ?? [];
    const backendHistory = item._variant === 'buffer'
      ? item.print_history_buffer
      : item.print_history_cases;
    if (backendHistory && backendHistory.length > 0) return backendHistory;
    const printed = isPrinted(item);
    if (!printed) return [];
    const weight = item._variant === 'buffer'
      ? (item.reserved_weight != null ? Number(item.reserved_weight) : null)
      : (item.actual_weight != null ? Number(item.actual_weight) : null);
    return [{
      barcode: null,
      printed_weight: weight,
      print_number: 1,
      printed_at: '',        // grandfathered row — no timestamp captured
      printed_by: null,      // nor a printer
      status: 'active',
    }];
  };

  // Green "Printed" / gray "Not printed" pill for the PRINTED column.
  const printedBadge = (item: DisplayItem) => (
    isPrinted(item) ? (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
        {t('printAudit.printed')}
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        {t('printAudit.notPrinted')}
      </span>
    )
  );

  // WCO-2623: per-row supervisor Reprint override. Only on an already-PRINTED row that needs a label
  // (FP-NI / RM-NI) — reprints & supersedes the current label (misplaced-label case). Unprinted rows
  // keep using the section "Print Labels" button.
  const reprintButton = (item: DisplayItem) =>
    (onReprint && rowNeedsLabel(item) && isPrinted(item)) ? (
      <button
        onClick={(e) => { e.stopPropagation(); onReprint(item); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
        title={t('dailyTransferOut.reprintOverrideTooltip')}
      >
        <Printer className="w-3 h-3" />
        {t('dailyTransferOut.reprint')}
      </button>
    ) : null;

  // WCO-2502: split each marketable by-product into separate "Cases" and "Buffer" rows so the
  // supervisor can review/print the full cases and the carry-forward reserve independently.
  // Only variants that actually have data are emitted (cases-only → one Cases row, buffer-only →
  // one Buffer row). Non-marketable rows pass through unchanged. WCO-2582: the transfer checkbox
  // renders ONLY on the Cases card (and non-marketable rows); the Buffer/reserve card is read-only
  // carry-forward with no checkbox, since the reserve is never shipped.
  const displayItems: DisplayItem[] = items.flatMap((item) => {
    if (item.category !== 'by_product_marketable') {
      return [{ ...item, _key: String(item.id) }];
    }
    const cases = Number(item.actual_quantity || 0);
    const caseWeight = Number(item.actual_weight || 0);
    const reserve = Number(item.reserved_weight || 0);
    const rows: DisplayItem[] = [];
    if (cases > 0 || caseWeight > 0) {
      rows.push({ ...item, _variant: 'cases', _key: `${item.id}-cases` });
    }
    if (reserve > 0) {
      rows.push({ ...item, _variant: 'buffer', _key: `${item.id}-buffer` });
    }
    if (rows.length === 0) {
      rows.push({ ...item, _key: String(item.id) });
    }
    return rows;
  });

  // WCO-2502: notes on the split cards carry only the "Latest session" context. Source material
  // has its own column and the case/reserve counts are the Cases/Buffer cards themselves, so drop
  // those segments (also handles rows still holding the older combined notes string).
  const NOTE_DROP_PREFIXES = ['Source material:', 'Ready cases:', 'Carry-forward reserve:'];
  const variantNotes = (item: DisplayItem): string => {
    if (!item.notes) return '';
    if (!item._variant) return item.notes;
    return item.notes
      .split(' | ')
      .filter((seg) => !NOTE_DROP_PREFIXES.some((p) => seg.startsWith(p)))
      .join(' | ');
  };

  // `flush` drops the left margin for use inside a gap-based flex row (mobile badge row).
  const variantBadge = (item: DisplayItem, flush = false) =>
    item._variant ? (
      <span
        className={`${flush ? '' : 'ml-2 '}inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
          item._variant === 'buffer'
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
        }`}
      >
        {item._variant === 'buffer' ? t('dailyTransferOut.bufferRow') : t('dailyTransferOut.casesRow')}
      </span>
    ) : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div
        className="px-5 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 rounded-t-lg flex items-center justify-between cursor-pointer select-none"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
            {title}
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({displayItems.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {headerAction}
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {!isCollapsed && (items.length === 0 ? (
        <div className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
          No items
        </div>
      ) : (
        <>
          {/* Desktop: full table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-max min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/30">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.itemNumber')}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.itemName')}
                  </th>
                  {showExpected && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.packing')}
                    </th>
                  )}
                  {showExpected && (
                    <>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                        {t('dailyTransferOut.expectedQty')}
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                        {t('dailyTransferOut.expectedWeight')}
                      </th>
                    </>
                  )}
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.actualQty')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.actualWeight')}
                  </th>
                  {showReserveColumn && (
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      Reserve
                    </th>
                  )}
                  {showExpected && (
                    <>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                        {t('dailyTransferOut.expectedYF')}
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                        {t('dailyTransferOut.actualYF')}
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                        {t('dailyTransferOut.variance')}
                      </th>
                    </>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.type')}
                  </th>
                  {showPrinted && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('printAudit.printedColumn')}
                    </th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.notes')}
                  </th>
                  {onToggleExclude && (
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.transfer') || 'Transfer'}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {displayItems.map((item) => {
                  // WCO-2582: the buffer/reserve card is read-only carry-forward, not a transfer
                  // decision, so it must never read as excluded — show its neutral category badge
                  // (never "Not Shipped") regardless of the shared `excluded` flag.
                  const rowStatus = item._variant === 'buffer' ? 'by-product-marketable' : getRowStatus(item);
                  const actualYF = showExpected
                    ? (item.actual_yield_percentage != null ? item.actual_yield_percentage : getItemActualYF(item))
                    : null;
                  const variancePct = showExpected
                    ? (item.yield_variance != null ? item.yield_variance : getItemYieldVariance(item))
                    : null;
                  const isBuffer = item._variant === 'buffer';

                  return (
                    <tr
                      key={item._key}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                        (item.excluded && !isBuffer) ? 'opacity-40' :
                        rowStatus === 'variance-warning'
                          ? 'bg-yellow-50/50 dark:bg-yellow-900/10'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {item.item_number}
                        {/* WCO-2618: (i) print-audit popover — shows the label print/reprint history so
                            it's clear why "Print Labels" is disabled. Only on rows that require a printed
                            label (per-row: FP-NI, remainder_not_intact, marketable by-products). */}
                        {rowNeedsLabel(item) && <PrintAuditPopover history={popoverHistory(item)} />}
                        {item.source_material_item_number && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Source: {item.source_material_item_number}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {item.item_chinese_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{item.item_chinese_name}</div>
                        )}
                        <div>{item.item_name || '-'}{variantBadge(item)}</div>
                      </td>
                      {showExpected && (
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {item.packing || '-'}
                        </td>
                      )}
                      {showExpected && (
                        <>
                          <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {item.expected_quantity != null
                              ? Number(item.expected_quantity).toLocaleString()
                              : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {item.expected_weight != null
                              ? Number(item.expected_weight).toFixed(2)
                              : '-'}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {isBuffer
                          ? '-'
                          : item.actual_quantity != null
                            ? Number(item.actual_quantity).toLocaleString()
                            : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {isBuffer
                          ? '-'
                          : item.actual_weight != null
                            ? Number(item.actual_weight).toFixed(2)
                            : '-'}
                      </td>
                      {showReserveColumn && (
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {item.category === 'by_product_marketable' && item._variant !== 'cases' && item.reserved_weight != null
                            ? Number(item.reserved_weight).toFixed(2)
                            : '-'}
                        </td>
                      )}
                      {showExpected && (
                        <>
                          <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {item.yield_factor != null && Number(item.yield_factor) > 0
                              ? formatYield(Number(item.yield_factor))
                              : '?'}
                          </td>
                          <td className={`px-4 py-2 text-sm text-right font-medium whitespace-nowrap ${getActualYFColor(item)}`}>
                            {actualYF != null ? formatYield(actualYF) : '-0%'}
                          </td>
                          <td className={`px-4 py-2 text-sm text-right font-medium whitespace-nowrap ${getYieldVarianceColor(variancePct, undefined)}`}>
                            {variancePct != null ? formatYieldVariancePct(variancePct) : '-0%'}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-2 whitespace-nowrap">{getStatusBadge(rowStatus)}</td>
                      {showPrinted && (
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {rowNeedsLabel(item) ? printedBadge(item) : null}
                            {reprintButton(item)}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        <div className="whitespace-nowrap">{variantNotes(item) || '-'}</div>
                      </td>
                      {onToggleExclude && (
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          {/* WCO-2582: the buffer/reserve is carry-forward, never shippable — only the
                              Cases card is a transfer decision, so no checkbox on the buffer row.
                              Empty cell preserves column alignment. */}
                          {!isBuffer && (
                            <input
                              type="checkbox"
                              checked={!item.excluded}
                              onChange={() => onToggleExclude(item)}
                              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card layout */}
          <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {displayItems.map((item) => {
              const rowStatus = getRowStatus(item);
              const actualYF = showExpected
                ? (item.actual_yield_percentage != null ? item.actual_yield_percentage : getItemActualYF(item))
                : null;
              const variancePct = showExpected
                ? (item.yield_variance != null ? item.yield_variance : getItemYieldVariance(item))
                : null;
              const isBuffer = item._variant === 'buffer';

              return (
                <div
                  key={item._key}
                  className={`px-4 py-3 ${
                    (item.excluded && !isBuffer) ? 'opacity-40' :
                    rowStatus === 'variance-warning'
                      ? 'bg-yellow-50/50 dark:bg-yellow-900/10'
                      : ''
                  }`}
                >
                  {/* Row 1: Item number (own line, no mid-word wrap) + transfer checkbox pinned right. */}
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-mono text-sm font-bold text-gray-900 dark:text-gray-100 break-normal">
                      {item.item_number}
                    </span>
                    {/* WCO-2582: no transfer checkbox on the buffer/reserve card — carry-forward, not shippable. */}
                    {onToggleExclude && !isBuffer && (
                      <input
                        type="checkbox"
                        checked={!item.excluded}
                        onChange={() => onToggleExclude(item)}
                        className="mt-0.5 w-4 h-4 shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                    )}
                  </div>
                  {/* Row 2: Item name */}
                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-1.5">
                    {item.item_chinese_name && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">{item.item_chinese_name}</span>
                    )}
                    {item.item_name || '-'}
                  </div>
                  {/* Row 3: badges — type / cases-buffer / printed + (i) — one tidy wrapping row. */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {getStatusBadge(rowStatus)}
                    {variantBadge(item, true)}
                    {rowNeedsLabel(item) && (
                      <span className="inline-flex items-center gap-0.5">
                        {printedBadge(item)}
                        <PrintAuditPopover history={popoverHistory(item)} />
                      </span>
                    )}
                    {reprintButton(item)}
                  </div>
                  {/* Row 3: Qty + Weight grid (Buffer card shows the reserve instead) */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                    {isBuffer ? (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('dailyTransferOut.reserve')}:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {item.reserved_weight != null ? Number(item.reserved_weight).toFixed(2) : '-'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t('dailyTransferOut.actualQty')}:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {item.actual_quantity != null ? Number(item.actual_quantity).toLocaleString() : '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t('dailyTransferOut.actualWeight')}:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {item.actual_weight != null ? Number(item.actual_weight).toFixed(2) : '-'}
                          </span>
                        </div>
                      </>
                    )}
                    {/* WCO-2618: mobile shows only Act. Qty + Act. Wgt — the Exp. YF / YF Variance
                        detail lives in the desktop table (dropped here to declutter the card). */}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ))}
    </div>
  );
};

export default CategoryTransferTable;
